package com.meistercharts.charts.pv.roofPlanning

import com.meistercharts.algorithms.painter.FillAndStrokeStyle
import com.meistercharts.canvas.interaction.ElementVisualState
import com.meistercharts.color.Color
import com.meistercharts.color.RgbaColor
import com.meistercharts.color.withAlpha

/**
 * How an [UnusableArea] is painted in one [ElementVisualState].
 */
data class UnusableAreaStyle(
  /**
   * If set to true the location and size are shown
   */
  val showLocationAndSize: Boolean,

  /**
   * Returns true if the delete icon is shown.
   * Attention: this is different from [showArmedDeleteIcon]
   */
  val showDeleteIcon: Boolean,

  val showArmedDeleteIcon: Boolean,

  /**
   * The fill and stroke that is used for that state
   */
  val fillAndStroke: FillAndStrokeStyle,
) {
  companion object {
    /**
     * An unusable area the user is not interacting with is still visible - it is part of the roof, not an overlay.
     */
    val Normal: UnusableAreaStyle = UnusableAreaStyle(
      showLocationAndSize = false,
      showDeleteIcon = false,
      showArmedDeleteIcon = false,
      fillAndStroke = FillAndStrokeStyle(Color.white().withAlpha(0.5), RgbaColor(86, 86, 86)),
    )

    val Hover: UnusableAreaStyle = UnusableAreaStyle(
      showLocationAndSize = true,
      showDeleteIcon = true,
      showArmedDeleteIcon = false,
      fillAndStroke = FillAndStrokeStyle(Color.web("#75b72633"), Color.web("#75b726")),
    )

    val Selected: UnusableAreaStyle = UnusableAreaStyle(
      showLocationAndSize = true,
      showDeleteIcon = false,
      showArmedDeleteIcon = false,
      fillAndStroke = FillAndStrokeStyle(Color.web("#75b7264D"), Color.web("#75b726")),
    )

    val SelectedHovering: UnusableAreaStyle = UnusableAreaStyle(
      showLocationAndSize = true,
      showDeleteIcon = true,
      showArmedDeleteIcon = false,
      fillAndStroke = FillAndStrokeStyle(Color.web("#75b7267D"), Color.web("#75b726")),
    )

    val Dragging: UnusableAreaStyle = UnusableAreaStyle(
      showLocationAndSize = true,
      showDeleteIcon = false,
      showArmedDeleteIcon = false,
      fillAndStroke = FillAndStrokeStyle(Color.white().withAlpha(0.2), Color.white()),
    )

    val DeleteArmed: UnusableAreaStyle = UnusableAreaStyle(
      showLocationAndSize = false,
      showDeleteIcon = false,
      showArmedDeleteIcon = true,
      fillAndStroke = FillAndStrokeStyle(Color.red().withAlpha(0.3), Color.red()),
    )

    fun forVisualState(visualState: ElementVisualState): UnusableAreaStyle {
      return when (visualState) {
        ElementVisualState.Normal -> Normal
        ElementVisualState.Hover -> Hover
        ElementVisualState.Selected -> Selected
        ElementVisualState.SelectedHovering -> SelectedHovering
        ElementVisualState.Dragging -> Dragging
        ElementVisualState.DeleteArmed -> DeleteArmed
        //An unusable area has no rotate button - arming the rotation of a module area must not change it
        ElementVisualState.RotateArmed -> Normal
      }
    }
  }
}
